import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'

type Props = {
  label: string
  required?: boolean
  menuItems?: {
    label: string
    value: string
  }[]
  handleChange: (item: SelectChangeEvent) => void
  value: string
  error?: boolean
  errorText?: string
  placeholder?: string
  customRenderValue?: (selected: string) => React.ReactNode
}

export default function Selectfield({
  label,
  error,
  errorText,
  required,
  menuItems,
  handleChange,
  value,
  placeholder,
  customRenderValue
}: Props) {
  const inputId = `${label.toLowerCase().replace(/\s+/g, '-')}-select`;
  const labelId = `${inputId}-label`;

  const showLabel = !placeholder; // avoid label/placeholder overlay

  return (
    <FormControl fullWidth error={error}>
      {showLabel && (<InputLabel id={labelId}>{label}</InputLabel>)}
      <Select
        labelId={labelId}
        id={inputId}
        required={required}
        value={value}
        label={showLabel ? label : undefined}
        className="select-field-component"
        error={error}
        displayEmpty
        renderValue={(selected) => {
          if (customRenderValue) {
            return customRenderValue(selected as string);
          }
          if (!selected || (typeof selected === 'string' && selected.length === 0)) {
            return placeholder ? <em>{placeholder}</em> : ''
          }

          // Find the label that corresponds to the selected value
          const selectedItem = menuItems?.find(item => item.value === selected);
          return selectedItem ? selectedItem.label : selected as string;
        }}
        inputProps={{
          'aria-label': label,
          'aria-required': required,
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
              width: 'auto'
            }
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          },
        }}
        sx={{
          '& fieldset': {
            borderRadius: '8px',
            border: '1px solid #E6E8EF'
          },
          '&:hover fieldset': {
            border: '1px solid #ebac6e !important'
          },
          marginBottom: '16px',
          '& .MuiSelect-select': {
            fontFamily: 'sofia-regular !important',
            textAlign: 'start',
            color: '#2A2F4E'
          },
          // Ensure the menu doesn't overlay the trigger awkwardly
          '& .MuiSelect-icon': {
            right: 12
          },
          '&:focus-within': {
            '& fieldset': {
              border: '1px solid #ebac6e !important'
            }
          }
        }}
        onChange={handleChange}
      >
        {/* No explicit placeholder item to prevent overlay; renderValue handles it */}
        {menuItems && menuItems.length > 0 &&
          menuItems.map((el, index) => (
            <MenuItem key={`${el.value}-${index}`} value={el.value}>
              {el.label}
            </MenuItem>
          ))
        }
      </Select>
      {error && errorText && <FormHelperText error={error}>{errorText}</FormHelperText>}
    </FormControl>
  )
}